import React, {useMemo, useRef, useEffect} from 'react'
import _ from 'lodash'

import moment from 'moment'
import AllDayEvents from './AllDayEvents'
import DateTimeIndicator from './DateTimeIndicator'
import DayHead from './DayHead'
import DayColumn from './DayColumn'
import TimeColumn from './TimeColumn'
import {snap} from '../../utils/ApMath'
import useAllDayReserveAction from './useAllDayReserveAction'
import WalkingPanda from '../../common/LoadingIndicators/WalkingPanda'
import {useState} from 'reactn'
import useInitLoader from './useInitLoader'

const Day = ({
  onClickEvent = () => {},
  onNavigate = () => {},
  showSidebar,
  events,
  selectedDate,
  willLoad = true,
}) => {
  const refMain = useRef(null)

  const [showLoader, doneInit] = useInitLoader(events.isLoading)

  const [todaysEvents, allDayEvents] = useMemo(() => {
    const hoursInMS = 60 * 60 * 1000
    const dayInMS = hoursInMS * 24
    let todaysEvents = []
    let allDayEvents = []

    events.data.forEach(ev => {
      // DummyEvents.forEach(ev => {
      const start_date = moment(ev.start)
      let real_end = moment(ev.end)
      let end_date = moment(ev.action_return_date || ev.end)

      // fix if fulfilled overdue
      if (
        ev.type === 'SystemReservationAction' &&
        ev.state === 'overdue' &&
        ev.status === 'fulfilled'
      ) {
        end_date = moment()
      }

      let eventDateRef = moment(Math.max(start_date.valueOf(), selectedDate.valueOf()))
        .startOf('day')
        .valueOf()

      let top = ((start_date.valueOf() - eventDateRef) / dayInMS) * 100
      let bottom = ((end_date.valueOf() - eventDateRef) / dayInMS) * 100

      if ((top < 0 && bottom <= 0) || (top >= 100 && bottom >= 100)) {
        return
      }

      // fix titles.
      let title = ''
      if (ev.display_name_first || ev.display_with_secondary || ev.display_name_third) {
        if (ev.type === 'SystemReservationAction') {
          title = `${ev.display_name_first}, ${ev.display_with_secondary}, ${ev.display_name_third}`
        } else if (ev.type === 'ReservationAction') {
          title = `${ev.display_name_first}, ${ev.display_with_secondary}, ${ev.display_name_third}`
        } else {
          title = `${ev.display_name_first}, ${ev.display_with_secondary}, ${ev.display_name_third}`
          let _startSnap = snap(
            start_date.valueOf() - selectedDate.valueOf(),
            hoursInMS * 1,
            Math.floor
          )
          top = (_startSnap / dayInMS) * 100
          bottom = ((_startSnap + hoursInMS * 1) / dayInMS) * 100
        }
      } else {
        if (ev.type === 'SystemReservationAction') {
          title = `#${ev?.reservation_uid || ev?.entity_display_name}, ${
            ev.reserved_for
          }, ${start_date.format('MMMM Do')} - ${real_end.format('MMMM Do')}, Items (${ev.items})`
        } else if (ev.type === 'ReservationAction') {
          title = `${ev.entity_display_name}, ${ev.display_name}, ${start_date.format(
            'MMMM Do'
          )} - ${real_end.format('MMMM Do')}`
        } else if (ev.type === 'DefaultAction') {
          title = `${ev.entity_display_name}, ${ev.display_name}, ${start_date.format('MMMM Do')}`
          // snap to floor ms
          let _startSnap = snap(
            start_date.valueOf() - selectedDate.valueOf(),
            hoursInMS * 0.5,
            Math.floor
          )
          top = (_startSnap / dayInMS) * 100
          bottom = ((_startSnap + hoursInMS * 0.5) / dayInMS) * 100
        } else {
          title = `${ev.entity_display_name || ev.display_name}, ${
            ev.field_name
          }, ${start_date.format('MMMM Do')}`

          // snap to floor ms
          let _startSnap = snap(
            start_date.valueOf() - selectedDate.valueOf(),
            hoursInMS * 0.5,
            Math.floor
          )
          top = (_startSnap / dayInMS) * 100
          bottom = ((_startSnap + hoursInMS * 0.5) / dayInMS) * 100
        }
      }

      if (top < 0 || bottom >= 100) {
        // all day
        allDayEvents.push({
          event: ev,
          top: top,
          bottom: bottom,
          title,
        })
      } else {
        todaysEvents.push({
          event: ev,
          top: top,
          bottom: bottom,
          left: 0,
          right: 0,
          title,
        })
      }
    })

    allDayEvents.sort((a, b) => {
      if (a.bottom - a.top > b.bottom - b.top) return -1
      else if (a.bottom - a.top === b.bottom - b.top && a.top < b.top) return -1
      else return 1
    })

    return [todaysEvents, [allDayEvents]]
  }, [events.data, selectedDate])

  const now = moment()

  // scrolling
  useEffect(() => {
    if (!refMain.current) return
    let _now = now.clone().startOf('day')
    let _selDate = selectedDate.clone().startOf('day')
    if (_selDate.valueOf() === _now.valueOf()) {
      // trigger scrolling
      const rect = refMain.current.getBoundingClientRect()
      const hours =
        (now.valueOf() -
          now
            .clone()
            .startOf('day')
            .valueOf()) /
        (60 * 60 * 1000)

      refMain.current.scrollTop = hours * 64 - rect.height * 0.5
    }
  }, [now, selectedDate])

  useAllDayReserveAction('.c-calendar-day [data-x="all-day-range"]', selectedDate, 1)

  return (
    <div className="c-calendar-day ap-no-select">
      <div className="header">
        <div className="time-column"></div>
        <div className="day-columns" data-x="all-day-range">
          <div className="day-column" data-x="all-day-range">
            <DayHead
              date={selectedDate}
              now={moment()}
              onClick={e => onNavigate(e, selectedDate, events)}
            />
          </div>
        </div>
      </div>
      {<WalkingPanda hidden={!showLoader || !willLoad} style={{top: 70, paddingTop: 120}} />}
      <AllDayEvents data={allDayEvents} selectedDate={selectedDate} onClickEvent={onClickEvent} />
      <div className="main ap-scrollbar" ref={refMain}>
        <div className="main-wrapper">
          <DateTimeIndicator now={now} selectedDate={selectedDate} dayCount={1} />
          <TimeColumn key={`tc_${showSidebar}`} />
          <DayColumn
            date={selectedDate}
            key={`dc_${showSidebar}`}
            events={todaysEvents}
            onClickEvent={onClickEvent}
            onClickMoreSidebar={e => onNavigate(e, selectedDate, events)}
          />
        </div>
      </div>
    </div>
  )
}

export default Day
